// @flow
import React, {useState, useEffect} from 'react';
import CustomModal from '../../../../../elements/CustomModal/CustomModal';
import {makeStyles, Typography} from '@material-ui/core';
import PropTypes from 'prop-types';
import {useAdminAddress} from '../../../../address/utils/useAdminAddress';
import CustomButtom from '../../../../../elements/CustomButton/CustomButton';
import CustomSelect from '../../../../../elements/CustomSelect/CustomSelect';
import {useStoreCountry} from '../../../../country/utils';
import {getConfigurationAdmin} from '../../../../../utils/location';
import {useForm, Controller} from 'react-hook-form';
import type {Country} from '../../../../../entities/country';

const useStyles = makeStyles(theme => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: theme.spacing(3),
    gap: theme.spacing(2),
    width: 400,
  },
  title: {
    fontWeight: 600,
    fontSize: '1.375rem',
  },
  subTitle: {
    fontWeight: 600,
    // fontSize: '1.125rem',
  },

  container: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    maxHeight: 450,
    overflowY: 'auto',

    '&::-webkit-scrollbar': {
      width: '8px',
    },
    '&::-webkit-scrollbar-track': {
      background: 'transparent',
    },
    '&::-webkit-scrollbar-thumb': {
      background: '#ccc',
      borderRadius: '10px',
    },
    '&::-webkit-scrollbar-thumb:hover': {
      background: '#b3b3b3',
    },
  },
  element: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    gap: theme.spacing(1),
  },
  row: {
    display: 'flex',
    alignItems: 'center',
  },
  leftContent: {
    display: 'flex',
    alignItems: 'start',
  },
}));

type Props = {
  typeFilter: 'national' | 'international',
  open: boolean,
  handleCloseModal: () => void,
  handleFilter: (filter: any) => void,
  onlyCountries?: boolean,
  showAdmin3?: boolean,
  ignoreStoreCountryP?: boolean,
  filterIsEmpty?: boolean,
  availableShippingCountries: Country[],
};

export default function FilterModalShipping(props: Props) {
  const {
    typeFilter,
    open,
    onlyCountries = false,
    showAdmin3 = true,
    ignoreStoreCountryP = false,
    handleCloseModal,
    handleFilter,
    filterIsEmpty,
    availableShippingCountries,
  } = props;
  const classes = useStyles();
  const country = useStoreCountry();
  const {watch, control, errors} = useForm();

  const [countriesList, setCountriesList] = useState<Country[]>([]);
  const [countrySelected, setCountrySelected] = useState('none');
  const watchCountry = watch('country', countrySelected);

  const adminConfigCountry = getConfigurationAdmin(
    watchCountry || country?.code
  );

  const {
    admin1List,
    admin2List,
    admin3List,
    selectAdmin1,
    selectAdmin2,
    selectAdmin3,
    selectedAdmin1,
    selectedAdmin2,
    selectedAdmin3,
    resetSelectedAdmins,
  } = useAdminAddress(watchCountry === 'none' ? undefined : watchCountry, {
    ignoreStoreCountry: ignoreStoreCountryP,
  });
  const handleFilterModal = () => {
    handleFilter({
      admin1: selectedAdmin1,
      admin2: selectedAdmin2,
      admin3: selectedAdmin3,
      countryCode: watchCountry,
    });
    handleCloseModal();
  };

  useEffect(() => {
    if (availableShippingCountries && country) {
      const countriesFilter = availableShippingCountries.filter(
        countryG => countryG.id !== country.id
      );
      // $FlowFixMe
      setCountriesList(countriesFilter);
    }
  }, [availableShippingCountries, country]);

  useEffect(() => {
    if (watchCountry) {
      setCountrySelected(watchCountry);
    }
  }, [watchCountry]);

  useEffect(() => {
    if (filterIsEmpty) {
      setCountrySelected('none');
      resetSelectedAdmins();
    }
  }, [filterIsEmpty, resetSelectedAdmins]);

  return (
    <CustomModal open={open} handleCloseModal={handleCloseModal}>
      <div className={classes.root}>
        <Typography align="center" gutterBottom className={classes.title}>
          Filtros
        </Typography>
        {typeFilter === 'international' && (
          <div className={classes.element}>
            <Typography className={classes.subTitle}>País</Typography>
            <Controller
              name="country"
              control={control}
              defaultValue={countrySelected || 'none'}
              rules={{required: 'Campo obligatorio'}}
              as={
                <CustomSelect
                  native
                  fullWidth
                  className={classes.select}
                  error={!!errors.country}
                  helperText={errors.country ? errors.country.message : ''}
                >
                  <option value={'none'} hidden />
                  {countriesList.map((c: Country, index) => (
                    <option key={`country-${c.code}`} value={c.code}>
                      {c.name}
                    </option>
                  ))}
                </CustomSelect>
              }
            />
          </div>
        )}

        {!onlyCountries && (
          <>
            <div className={classes.element}>
              <Typography className={classes.subTitle}>
                {adminConfigCountry?.admin1?.label || 'Departamento'}
              </Typography>
              <CustomSelect
                native
                fullWidth
                value={
                  selectedAdmin1?.adminId !== undefined
                    ? selectedAdmin1.adminId
                    : 'none'
                }
                onChange={e => {
                  const adminId = e.target.value;
                  if (adminId === 'none') return;

                  const adminAddress = admin1List.find(
                    admin => admin.adminId.toString() === adminId
                  );
                  if (!adminAddress) return;

                  selectAdmin1(adminAddress);
                }}
              >
                <option value="none" hidden />
                {admin1List.map(c => (
                  <option
                    key={`admin1-${c.adminId}`}
                    value={c.adminId.toString()}
                  >
                    {c.adminName}
                  </option>
                ))}
              </CustomSelect>
            </div>
            {adminConfigCountry?.admin2?.enabled && (
              <div className={classes.element}>
                <Typography className={classes.subTitle}>
                  {adminConfigCountry?.admin2?.label || 'Provincia'}
                </Typography>
                <CustomSelect
                  native
                  fullWidth
                  disabled={selectedAdmin1?.adminId === undefined}
                  value={selectedAdmin2?.adminId || 'none'}
                  onChange={e => {
                    const adminId = e.target.value;
                    const adminAddress = admin2List.find(
                      admin => admin.adminId.toString() === adminId
                    );

                    if (!adminAddress) return;

                    selectAdmin2(adminAddress);
                  }}
                >
                  <option value={'none'} hidden />
                  {admin2List.map((c, index) => (
                    <option key={`admin2-${c.adminId}`} value={c.adminId}>
                      {c.adminName}
                    </option>
                  ))}
                </CustomSelect>
              </div>
            )}
            {adminConfigCountry?.admin3?.enabled && showAdmin3 && (
              <div className={classes.element}>
                <Typography className={classes.subTitle}>
                  {adminConfigCountry?.admin3?.label || 'Ciudad'}
                </Typography>
                <CustomSelect
                  native
                  fullWidth
                  disabled={selectedAdmin2?.adminId === undefined}
                  value={selectedAdmin3?.adminId || 'none'}
                  onChange={e => {
                    const adminId = e.target.value;
                    const adminAddress = admin3List.find(
                      admin => admin.adminId.toString() === adminId
                    );

                    if (!adminAddress) return;

                    selectAdmin3(adminAddress);
                  }}
                >
                  <option value={'none'} hidden />
                  {admin3List.map((c, index) => (
                    <option key={`admin3-${c.adminId}`} value={c.adminId}>
                      {c.adminName}
                    </option>
                  ))}
                </CustomSelect>
              </div>
            )}
          </>
        )}

        <CustomButtom
          variant="contained"
          color="primary"
          onClick={handleFilterModal}
        >
          Aplicar
        </CustomButtom>
      </div>
    </CustomModal>
  );
}

FilterModalShipping.propTypes = {
  typeFilter: PropTypes.oneOf(['national', 'international']).isRequired,
  open: PropTypes.bool.isRequired,
  handleCloseModal: PropTypes.func.isRequired,
  handleFilter: PropTypes.func.isRequired,
  onlyCountries: PropTypes.bool,
  showAdmin3: PropTypes.bool,
  ignoreStoreCountryP: PropTypes.bool,
  filterIsEmpty: PropTypes.bool,
};
